// WIP math — computed client-side from the raw read model so the same numbers
// recompute live when a PM edits cost-to-complete (Phase 3). Formulas per the
// design's calculation reference.
import type { FinancialsWip, WipCostRow } from './apiClient';

export interface WipRow extends WipCostRow {
  // budget = base + delta (transfers). WipCostRow.budget carries the ADJUSTED
  // value; budgetBase/budgetDelta expose the split for the delta chip.
  budgetBase: number;      // estimate budget before any transfer
  budgetDelta: number;     // net budget-transfer adjustment (+/−)
  ctc: number;             // cost to complete — default = remaining ADJUSTED budget (≥0), $0 once released
  ctcOverridden: boolean;  // a PM CTC override applies to this code
  eac: number;             // estimate at completion = actual + ctc
  overUnder: number;       // eac − budget  (+ = over budget)
  costPct: number;         // actual / eac  (cost-basis % complete for the row)
}
export interface WipTotals { budget: number; committed: number; actual: number; ctc: number; eac: number; overUnder: number; }
export interface WipSummary {
  revised: number; billed: number;
  eac: number; budget: number;
  pctComplete: number;       // actual / eac
  earned: number;            // pctComplete × revised
  overUnderBilled: number;   // billed − earned  (+ overbilled, − underbilled)
  projectedProfit: number;   // revised − eac
  projectedMarginPct: number;
  // Super Margin = project margin BEFORE (allocated) indirects. The GL allocated-
  // indirect cost (account 59998) is carved back out of the projection, so
  // superProfit = projectedProfit + indirectActual. Product owner, 2026-07-22.
  indirectActual: number;    // allocated indirect $ in EAC (GL 59998); can be ±
  superProfit: number;       // revised − (eac − indirectActual)
  superMarginPct: number;
  estimateProfit: number;    // revised − budget
  estimateMarginPct: number;
  fadeDollars: number;       // projectedProfit − estimateProfit  (= budget − eac)
  fadePts: number;           // projectedMarginPct − estimateMarginPct
  costVariance: number;      // eac − budget
  leftToBill: number;        // revised − billed
}

/** Default CTC = remaining (adjusted) budget, never negative — or $0 when the
 *  job's remaining budget is released (see `ComputeOpts.releaseRemaining`). */
export function defaultCtc(budget: number, actual: number, releaseRemaining = false): number {
  if (releaseRemaining) return 0;
  return Math.max(budget - actual, 0);
}

export interface ComputeOpts {
  ctc?: Record<string, number>;        // PM CTC overrides by cost code
  budgetAdj?: Record<string, number>;  // net budget-transfer delta by cost code
  /**
   * The job is Completed in Intacct, so budget left on a cost code is not cost
   * still to come: the DEFAULT cost to complete is $0, not the remaining budget.
   * Accounting's rule (controller, 2026-10-06) — a Completed job means the PM has
   * said the work is finished with no open commitments, and the remaining budget
   * is not going to be spent. Without it a finished job keeps every unspent dollar
   * in EAC and reads as a loss it did not have (#603).
   *
   * ⚠️ Only the DEFAULT moves. A typed override or a buildup is a number someone
   * entered, so it still wins — releasing must never overwrite a PM's forecast.
   * Overruns are untouched too: a code over budget already contributes its actual.
   *
   * ⚠️ THIS IS MIRRORED in `getPortfolioWip` (apps/api/src/financials/
   * financials.service.ts), which rolls the same default up server-side. The two
   * must agree or the report and the tab show different margins for one job.
   * The server decides "Completed" (`jobCompleted` on the WIP read model, stored
   * on every snapshot) so this app carries no copy of what counts as Completed.
   */
  releaseRemaining?: boolean;
}
export function computeRows(rows: WipCostRow[], opts: ComputeOpts = {}): WipRow[] {
  return rows.map((r) => {
    const code = r.costCode ?? '';
    const base = r.budget ?? 0;
    const delta = (code ? opts.budgetAdj?.[code] : undefined) ?? 0;
    const budget = base + delta;
    const actual = r.actual ?? 0;
    const override = code ? opts.ctc?.[code] : undefined;
    const ctcOverridden = override != null && Number.isFinite(override);
    const ctc = ctcOverridden ? Number(override) : defaultCtc(budget, actual, opts.releaseRemaining === true);
    const eac = actual + ctc;
    return {
      ...r, budget, budgetBase: base, budgetDelta: delta,
      ctc, ctcOverridden, eac,
      overUnder: eac - budget, costPct: eac ? (actual / eac) * 100 : 0,
    };
  });
}

export function totals(rows: WipRow[]): WipTotals {
  return rows.reduce<WipTotals>((t, r) => ({
    budget: t.budget + (r.budget ?? 0), committed: t.committed + (r.committed ?? 0),
    actual: t.actual + (r.actual ?? 0), ctc: t.ctc + r.ctc, eac: t.eac + r.eac,
    overUnder: t.overUnder + r.overUnder,
  }), { budget: 0, committed: 0, actual: 0, ctc: 0, eac: 0, overUnder: 0 });
}

export function summarize(t: WipTotals, contract: FinancialsWip['contract'], indirectActual = 0): WipSummary {
  const revised = contract?.revised ?? 0;
  const billed = contract?.billed ?? 0;
  const eac = t.eac, budget = t.budget;
  const pctComplete = eac ? t.actual / eac : 0;
  const earned = pctComplete * revised;
  const projectedProfit = revised - eac;
  const estimateProfit = revised - budget;
  const projectedMarginPct = revised ? projectedProfit / revised : 0;
  const estimateMarginPct = revised ? estimateProfit / revised : 0;
  // Super Margin — margin before allocated indirects. The indirect allocation
  // (GL 59998) is inside actual/EAC; carve it back out. superProfit is thus
  // ≥ projectedProfit when the allocation is positive (typical), and < when a
  // job carries a net-negative allocation.
  const superProfit = projectedProfit + indirectActual;
  const superMarginPct = revised ? superProfit / revised : 0;
  return {
    revised, billed, eac, budget,
    pctComplete, earned,
    overUnderBilled: billed - earned,
    projectedProfit, projectedMarginPct,
    indirectActual, superProfit, superMarginPct,
    estimateProfit, estimateMarginPct,
    fadeDollars: projectedProfit - estimateProfit,
    fadePts: projectedMarginPct - estimateMarginPct,
    costVariance: eac - budget,
    leftToBill: revised - billed,
  };
}

/**
 * A CTC buildup line's amount, recomputed client-side while the PM types.
 *
 * ⚠️ THIS MIRRORS THE API's `ctcLineAmount` (financials/lib/ctcBuildup) and the
 * two must agree. The server's value is authoritative and is what gets stored;
 * this exists only so the drawer can show a running total before a save. There
 * is no shared package between the apps, so the duplication is real — both
 * copies are unit-tested against the same cases to keep them pinned together.
 *
 * A blank factor is 1, never 0: absent means "this dimension doesn't apply to
 * this kind of line", and zeroing would silently void every Material line.
 */
export function ctcLineAmount(l: {
  qty1?: number | string | null; qty2?: number | string | null;
  qty3?: number | string | null; rate?: number | string | null;
}): number {
  const f = (v: number | string | null | undefined) => {
    if (v == null || String(v).trim() === '') return 1;
    const n = Number(v);
    return Number.isFinite(n) ? n : 1;
  };
  const r = l.rate == null || String(l.rate).trim() === '' ? 0 : Number(l.rate);
  const rate = Number.isFinite(r) ? r : 0;
  // Rounded per line so the drawer's total equals the sum of what's on screen.
  return Math.round(f(l.qty1) * f(l.qty2) * f(l.qty3) * rate * 100) / 100;
}

/**
 * Why some over-budget codes are over: the allocated-indirect (GL 59998) share.
 *
 * The estimate budgets nothing to 59998, so allocation landing on a code has no
 * budget to sit against and pushes it over. Finance confirmed the treatment is
 * correct and matches Intacct (2026-07-31) — this explains the overrun, it does
 * not adjust it.
 *
 * ⚠️ Counts only POSITIVE indirect. An allocation can be a credit (see
 * `WipSummary.indirectActual`, documented as ±), and a credit reduces actual, so
 * it can never be the reason a code is over budget. Treating `!== 0` as
 * "carries indirect" would both inflate the code count and net credits against
 * costs in the total. Extracted here from the component so it can be tested.
 */
export interface IndirectOverrun {
  /** Over-budget codes carrying positive allocated indirect. */
  codes: WipRow[];
  /** Of those, the ones the indirect alone accounts for — remove it and they're
   *  within budget, so there is no crew overrun to chase. */
  onlyFromIndirect: WipRow[];
  /** Total positive indirect across `codes`. */
  total: number;
}
export function indirectOverrun(overBudget: readonly WipRow[]): IndirectOverrun {
  const codes = overBudget.filter((r) => (r.indirect ?? 0) > 0);
  return {
    codes,
    onlyFromIndirect: codes.filter((r) => r.overUnder <= (r.indirect ?? 0)),
    total: codes.reduce((sum, r) => sum + (r.indirect ?? 0), 0),
  };
}

/** Pivot the COMPUTED rows for the Job Cost Detail table. Operating on the
 *  fine-grained (cost code × cost type) computed rows means all three groupings
 *  reconcile to the same totals. */
export type GroupBy = 'type' | 'code';
export interface GroupRow {
  key: string; label: string; sub: string | null;
  budget: number; budgetBase: number; budgetDelta: number;
  committed: number; actual: number; ctc: number; eac: number; overUnder: number; costPct: number;
  /** Allocated indirect (GL 59998) already inside `actual`. Shown so a PM can
   *  see why a code reads above budget; never subtracted from anything. */
  indirect: number;
  unbudgeted: boolean;     // has cost but the estimate never budgeted this code/group
  ctcOverridden: boolean;  // a PM CTC override applies (any row in the group)
  /** The cost codes that produced this row, verbatim (`''` = the null-code
   *  bucket). The GL drill-down filters on these so the lines it lists are
   *  exactly the ones that summed to this row's Actual — deriving the set a
   *  second time is how a drawer subtotal ends up disagreeing with the cell
   *  above it. */
  codes: string[];
}
export function groupRows(rows: WipRow[], by: GroupBy): GroupRow[] {
  const m = new Map<string, GroupRow>();
  for (const r of rows) {
    let key: string, label: string, sub: string | null = null;
    if (by === 'type') { key = r.costTypeGroup; label = r.costTypeGroup; }
    else { key = r.costCode ?? '—'; label = r.costCode ?? '—'; sub = r.costCodeName; }
    const g = m.get(key) ?? { key, label, sub, budget: 0, budgetBase: 0, budgetDelta: 0, committed: 0, actual: 0, indirect: 0, ctc: 0, eac: 0, overUnder: 0, costPct: 0, unbudgeted: false, ctcOverridden: false, codes: [] };
    g.codes.push(r.costCode ?? '');
    g.budget += r.budget ?? 0; g.budgetBase += r.budgetBase; g.budgetDelta += r.budgetDelta;
    g.committed += r.committed ?? 0; g.actual += r.actual ?? 0; g.indirect += r.indirect ?? 0;
    g.ctc += r.ctc; g.eac += r.eac; g.overUnder += r.overUnder;
    if (r.ctcOverridden) g.ctcOverridden = true;
    if (!g.sub && sub) g.sub = sub;
    m.set(key, g);
  }
  const out = [...m.values()];
  out.forEach((g) => {
    g.costPct = g.eac ? (g.actual / g.eac) * 100 : 0;
    g.unbudgeted = g.budget <= 0 && (g.actual > 0 || g.committed > 0);
  });
  return out.sort(by === 'code' ? byCostCode : byBudgetThenLabel);
}

/** Cost types: the few groups, biggest budget first; equal budgets by name, in a fixed locale like the codes. */
const byBudgetThenLabel = (a: GroupRow, b: GroupRow) => b.budget - a.budget || compareLabels(a.label, b.label);

/**
 * Cost codes: in CODE order, so a PM can find a code the way they find it in the estimate.
 *
 * It used to be the budget order above for both groupings, which reads as random down a list of
 * dozens of codes (feedback #664). Codes look like `01-31-23`, zero-padded, so the plain order is
 * the numeric one; `numeric` keeps a code with an unpadded number (`2-1` before `10-1`) in step. The
 * bucket for cost with no code at all goes last, wherever its dollars would put it.
 */
const hasNoCode = (g: GroupRow) => g.key === '—' || g.key === '';
// One collator, with the locale NAMED: the viewer's browser locale must not change the order of a job's
// codes, and a collator is built once instead of resolving its options on every comparison.
const labelCollator = new Intl.Collator('en', { numeric: true });
/**
 * The numeric collator calls `2-1` and `02-1` EQUAL, and the rows arrive from queries with no ORDER BY, so a
 * tie would keep whatever order the database happened to return (Codex, #666). On a tie, fall back to the
 * plain text, which is never equal for two different codes, so the order does not depend on arrival order.
 */
const compareLabels = (a: string, b: string) => labelCollator.compare(a, b) || (a < b ? -1 : a > b ? 1 : 0);
const byCostCode = (a: GroupRow, b: GroupRow) => {
  if (hasNoCode(a) !== hasNoCode(b)) return hasNoCode(a) ? 1 : -1;
  return compareLabels(a.label, b.label);
};
